import { organizationConverter } from 'data/converters';
import {
  domainSuffixConverter,
  nonWorkingDaysConverter,
  notificationsNewCommentConverter,
  notificationsNewTicketConverter,
  organizationAddressEditConverter,
  organizationLogoEditConverter,
  organizationNameEditConverter,
  organizationWebsiteEditConverter,
  portalInformationEditConverter,
  portalSettingsConverter,
  portalSubdomainEditConverter,
} from 'data/converters/organization.converters';
import { TOrganizationData, TOrganizationDataUi } from 'data/types';
import {
  TDomainSuffix,
  TDomainSuffixUi,
  TEditOrganizationAddressUi,
  TEditOrganizationLogoUi,
  TEditOrganizationNameUi,
  TEditOrganizationWebsiteUi,
  TEditPortalInformation,
  TEditPortalInformationUi,
  TEditPortalSubdomain,
  TEditPortalSubdomainUi,
  TNonWorkingDaysUi,
  TNotificationsNewComment,
  TNotificationsNewCommentUi,
  TNotificationsNewTicket,
  TNotificationsNewTicketUi,
  TOverlappingTimeUi,
  TPortalSettings,
  TPortalSettingsUi,
} from 'data/types/organization.types';
import { authSplitApi } from 'redux/helpers/slice.helpers';

export const ORGANIZATION_URL = 'api/organizations';
const PORTAL_SETTINGS = 'api/organizations/client-portal-settings';

export const organizationsApi = authSplitApi('organizations', [
  'organization',
  'portalSuffix',
  'portalSettings',
  'notificationsNewTicket',
  'notificationsNewComment',
]).injectEndpoints({
  endpoints: build => ({
    postOrganizations: build.mutation<TOrganizationData, TOrganizationDataUi>({
      query(body) {
        return {
          url: ORGANIZATION_URL,
          method: 'POST',
          body: organizationConverter.toDb(body),
        };
      },
    }),
    putOrganizations: build.mutation<TOrganizationData, TOrganizationDataUi>({
      query(body) {
        return {
          url: `${ORGANIZATION_URL}/${body.id}`,
          method: 'PUT',
          body: organizationConverter.toDb(body),
        };
      },
      invalidatesTags: ['organization'],
    }),
    getOrganizationById: build.query<TOrganizationDataUi, string | undefined>({
      query(id) {
        return {
          url: `${ORGANIZATION_URL}/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['organization'],
      transformResponse: (data: TOrganizationData) =>
        organizationConverter.fromDb(data),
    }),
    getNotificationsNewTicket: build.query<TNotificationsNewTicketUi[], void>({
      query() {
        return {
          url: `${ORGANIZATION_URL}/notifications/new-ticket`,
          method: 'GET',
        };
      },
      providesTags: ['notificationsNewTicket'],
      transformResponse: (data: TNotificationsNewTicket[]) =>
        data.map(x => notificationsNewTicketConverter.fromDb(x)),
    }),
    getNotificationsNewComment: build.query<TNotificationsNewCommentUi[], void>(
      {
        query() {
          return {
            url: `${ORGANIZATION_URL}/notifications/new-comment`,
            method: 'GET',
          };
        },
        providesTags: ['notificationsNewComment'],
        transformResponse: (data: TNotificationsNewComment[]) =>
          data.map(x => notificationsNewCommentConverter.fromDb(x)),
      },
    ),
    searchOnTable: build.query<
      string[],
      {
        tableName: string;
        filedName: string;
        value: string;
      }
    >({
      query({ tableName, filedName, value }) {
        // TODO: create a converter for this that make a url from the query params
        const urlWithParams = ORGANIZATION_URL?.concat(
          `/search?tableName=${tableName}&filedName=${filedName}&value=${value}`,
        );

        return {
          url: urlWithParams,
          method: 'GET',
        };
      },
    }),
    getDomainSuffix: build.query<TDomainSuffixUi, void>({
      query() {
        return {
          url: `${PORTAL_SETTINGS}/domain-suffix`,
          method: 'GET',
        };
      },
      providesTags: ['portalSuffix'],
      transformResponse: (data: TDomainSuffix) =>
        domainSuffixConverter.fromDb(data),
    }),
    getPortalSettings: build.query<TPortalSettingsUi, void>({
      query() {
        return {
          url: `${PORTAL_SETTINGS}`,
          method: 'GET',
        };
      },
      providesTags: ['portalSettings'],
      transformResponse: (data: TPortalSettings) =>
        portalSettingsConverter.fromDb(data),
    }),
    editPortalSettings: build.mutation<
      TEditPortalSubdomainUi,
      TEditPortalSubdomain
    >({
      query(body) {
        return {
          url: `${PORTAL_SETTINGS}/domain`,
          method: 'PATCH',
          body: portalSubdomainEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['portalSettings'],
    }),
    editPortalInformation: build.mutation<
      TEditPortalInformationUi,
      TEditPortalInformation
    >({
      query(body) {
        return {
          url: `${PORTAL_SETTINGS}/portal-information`,
          method: 'PATCH',
          body: portalInformationEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['portalSettings'],
    }),
    editOrganizationLogo: build.mutation<
      TOrganizationData,
      TEditOrganizationLogoUi
    >({
      query(body) {
        return {
          url: `${ORGANIZATION_URL}/logo`,
          method: 'PATCH',
          body: organizationLogoEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['organization'],
    }),
    editOrganizationName: build.mutation<
      TOrganizationData,
      TEditOrganizationNameUi
    >({
      query(body) {
        return {
          url: `${ORGANIZATION_URL}/name`,
          method: 'PATCH',
          body: organizationNameEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['organization'],
    }),
    editOrganizationWebsite: build.mutation<
      TOrganizationData,
      TEditOrganizationWebsiteUi
    >({
      query(body) {
        return {
          url: `${ORGANIZATION_URL}/website`,
          method: 'PATCH',
          body: organizationWebsiteEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['organization'],
    }),
    editOrganizationAddress: build.mutation<
      TOrganizationData,
      TEditOrganizationAddressUi
    >({
      query(body) {
        return {
          url: `${ORGANIZATION_URL}/address`,
          method: 'PATCH',
          body: organizationAddressEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['organization'],
    }),
    setupNonWorkingDays: build.mutation<TOrganizationData, TNonWorkingDaysUi[]>(
      {
        query(body) {
          return {
            url: `${ORGANIZATION_URL}/non-working-days`,
            method: 'PATCH',
            body: body.map(el => nonWorkingDaysConverter.toDb(el)),
          };
        },
        invalidatesTags: ['organization'],
      },
    ),
    patchTicketReporterOnNotification: build.mutation<
      void,
      { enabled: boolean; id: string }
    >({
      query({ enabled, id }) {
        return {
          url: `${ORGANIZATION_URL}/notifications/ticket-reporter`,
          method: 'PATCH',
          body: { enabled, id },
        };
      },
      invalidatesTags: ['notificationsNewTicket', 'notificationsNewComment'],
    }),
    patchEnableOnNotification: build.mutation<
      void,
      { enabled: boolean; id: string }
    >({
      query({ enabled, id }) {
        return {
          url: `${ORGANIZATION_URL}/notifications/enable`,
          method: 'PATCH',
          body: { enabled, id },
        };
      },
      invalidatesTags: ['notificationsNewTicket', 'notificationsNewComment'],
    }),
    patchNotificationListValue: build.mutation<
      void,
      { value: string; id: string; enabled: boolean }
    >({
      query({ value, id, enabled }) {
        return {
          url: `${ORGANIZATION_URL}/notifications/notification-list`,
          method: 'PATCH',
          body: { value, id, enabled },
        };
      },
      invalidatesTags: ['notificationsNewTicket', 'notificationsNewComment'],
    }),
    getOverlappingTimeOverride: build.query<TOverlappingTimeUi, void>({
      query() {
        return {
          url: `${ORGANIZATION_URL}/overlapping-time`,
          method: 'GET',
        };
      },
      providesTags: ['overlapping_time'],
    }),
    postOverlappingTimeOverride: build.mutation<void, boolean>({
      query(enabled) {
        return {
          url: `${ORGANIZATION_URL}/overlapping-time`,
          method: 'POST',
          body: { enabled },
        };
      },
      invalidatesTags: ['overlapping_time'],
    }),
    getAvailableTimezones: build.query<string[], void>({
      query() {
        return {
          url: `${ORGANIZATION_URL}/timezones`,
          method: 'GET',
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  usePostOrganizationsMutation,
  usePutOrganizationsMutation,
  useGetOrganizationByIdQuery,
  useGetNotificationsNewTicketQuery,
  useGetNotificationsNewCommentQuery,
  useSearchOnTableQuery,
  useGetDomainSuffixQuery,
  useGetPortalSettingsQuery,
  useEditPortalSettingsMutation,
  useEditPortalInformationMutation,
  useEditOrganizationLogoMutation,
  useEditOrganizationNameMutation,
  useEditOrganizationWebsiteMutation,
  useEditOrganizationAddressMutation,
  useSetupNonWorkingDaysMutation,
  usePatchTicketReporterOnNotificationMutation,
  usePatchEnableOnNotificationMutation,
  usePatchNotificationListValueMutation,
  useGetOverlappingTimeOverrideQuery,
  usePostOverlappingTimeOverrideMutation,
  useGetAvailableTimezonesQuery,
} = organizationsApi;
